import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { getHistoryPost, getTeamMembers, getStaffMembers } from '../services/wordpress';
import { NewsItem } from '../types';
import { ArrowRight, Heart, Users, Loader2, Globe, Database, Building2, Quote, Map, ChevronRight, ChevronLeft } from 'lucide-react';
import { safeHTML } from '../utils/safeHTML';
import './AboutPage.css';


import { useNavigate } from 'react-router-dom';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const [historyPost, setHistoryPost] = useState<NewsItem | null>(null);
  const [boardMembers, setBoardMembers] = useState<NewsItem[]>([]);
  const [staffMembers, setStaffMembers] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentStaffIndex, setCurrentStaffIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(4);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else if (window.innerWidth < 1280) {
        setItemsPerView(3);
      } else {
        setItemsPerView(4);
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [history, team, staff] = await Promise.all([
          getHistoryPost(),
          getTeamMembers(),
          getStaffMembers()
        ]);

        setHistoryPost(history);

        // Filter Leaders
        const topLeaders = team.filter(m =>
          m.role?.toLowerCase().includes('ketua') ||
          m.role?.toLowerCase().includes('sekretaris')
        ).slice(0, 3);
        setBoardMembers(topLeaders);

        // Fallback Staff jika data dari WordPress kosong
        if (staff && staff.length > 0) {
          setStaffMembers(staff);
        } else {
          // Data dummy agar section tidak kosong saat development (ditambah menjadi 10 agar fitur "View All" terlihat)
          const dummyStaff = Array(10).fill(null).map((_, i) => ({
            id: `s-${i}`,
            title: `Staff Member ${i + 1}`,
            role: 'Program Officer',
            imageUrl: '',
            date: '',
            category: 'Staff',
            excerpt: ''
          }));
          setStaffMembers(dummyStaff);
        }
      } catch (err) {
        console.error("Gagal memuat data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <Loader2 className="animate-spin text-emerald-700" size={48} />
      </div>
    );
  }

  const valuesList = [
    "Transformatif dalam membangun gerakan",
    "Kesetaraan dalam membangun hubungan antarmanusia dan kerjasama",
    "Bijak dalam bersikap",
    "Demokratis dalam pengambilan keputusan",
    "Kritis dan reflektif dalam berpikir",
    "Inovatif dalam pengembangan program",
    "Partisipatif dalam pemberdayaan manusia",
    "Profesional dalam bekerja",
    "Akuntabel dalam kinerja",
    "Transparan dalam manajemen",
    "Realistis dalam perencanaan",
    "Efisien dan ramah lingkungan dalam pengelolaan sumberdaya",
    "Memiliki semangat pembelajar (“learning spirit”)"
  ];

  return (
    <div className="About_page">
      <Helmet>
        <title>Tentang Kami - Lakpesdam PBNU</title>
        <meta name="description" content="Profil, Sejarah, Visi Misi, dan Susunan Pengurus Lembaga Kajian dan Pengembangan Sumber Daya Manusia (Lakpesdam) Nahdlatul Ulama." />
        <meta property="og:title" content="Tentang Kami - Lakpesdam PBNU" />
        <meta property="og:description" content="Profil, Sejarah, Visi Misi, dan Susunan Pengurus Lakpesdam PBNU." />
        <meta property="og:image" content="https://lakpesdam.or.id/assets/og-image.png" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "Tentang Kami - Lakpesdam PBNU",
            "description": "Profil, Sejarah, Visi Misi, dan Susunan Pengurus Lakpesdam PBNU.",
            "url": "https://lakpesdam.or.id/?page=about",
            "mainEntity": {
              "@type": "Organization",
              "name": "Lakpesdam PBNU",
              "url": "https://lakpesdam.or.id"
            }
          })}
        </script>
      </Helmet>
      {/* 1. HERO SECTION */}
      <section className="About_hero">
        <div className="container">
          <div className="About_heroContent animate-in">
            <span className="About_heroLabel">Khidmat untuk Umat</span>
            <h1 className="About_title">Tentang Lakpesdam PBNU</h1>
            <p className="About_subtitle">
              Lembaga Kajian dan Pengembangan Sumber Daya Manusia<br className="hidden md:block" />
              Pengurus Besar Nahdlatul Ulama yang berkhidmat<br className="hidden lg:block" />
              untuk pemberdayaan umat dan bangsa.
            </p>
          </div>
        </div>
        <div className="About_heroDecoration"></div>
      </section>

      {/* 2. HISTORY SECTION */}
      <section className="About_history" id="history">
        <div className="container">
          <motion.div
            className="About_historyLayout"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <div className="About_historyImageWrapper">
              <img
                src={historyPost?.imageUrl || "https://images.unsplash.com/photo-1526493722709-663806a6c4b2?w=800"}
                alt="Sejarah Lakpesdam"
                className="About_historyImage"
                loading="lazy"
                width="800"
                height="600"
              />
              <div className="About_historyImageBadge">Sejak 1985</div>
            </div>
            <div className="About_historyInfo">
              <span className="About_sectionLabel">Profil & Perjalanan</span>
              <h2 className="About_sectionTitle">{historyPost?.title || "Mengakar Kuat, Menjulang Tinggi"}</h2>
              <div className="About_text">
                <div {...safeHTML(historyPost?.excerpt || "Lakpesdam PBNU didirikan sebagai respon terhadap kebutuhan pengembangan SDM di lingkungan Nahdlatul Ulama...")} />
              </div>
              <button onClick={() => navigate('/sejarah')} className="About_btn_primary mt-8 group">
                Baca Sejarah Selengkapnya <ArrowRight size={20} className="transition-transform group-hover:translate-x-1.5" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3. VISI MISI SECTION */}
      <section className="About_visimisi" id="visimisi">
        <div className="container">
          <motion.div
            className="About_vmGrid"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <div className="About_visiBox">
              <span className="About_sectionLabel text-amber-400">Visi Kami</span>
              <h2 className="About_vmTitle">Visi Besar</h2>
              <div className="About_visiQuoteBox">
                <Quote className="About_quoteIcon" size={32} />
                <p className="About_visiText">
                  Menjadi lembaga kajian dan pengembangan SDM yang terkemuka dalam mewujudkan kehidupan yang berkeadilan, demokratis, dan berkeadaban dengan nilai-nilai Islam Aswaja An-Nahdliyah.
                </p>
              </div>
            </div>
            <div className="About_misiBox">
              <span className="About_sectionLabel text-amber-400">Misi Kami</span>
              <h2 className="About_vmTitle">Misi Strategis</h2>
              <div className="About_misiList">
                {[
                  { icon: <Heart size={20} />, t: "Pengarusutamaan Aswaja", d: "Moderasi dan inklusivitas Islam Nusantara." },
                  { icon: <Globe size={20} />, t: "Advokasi Kebijakan", d: "Mempengaruhi kebijakan publik berbasis data." },
                  { icon: <Users size={20} />, t: "Pemberdayaan SDM", d: "Membangun manusia yang unggul and responsif." },
                  { icon: <Database size={20} />, t: "Manajemen Pengetahuan", d: "Pengetahuan sebagai basis penguatan program." },
                  { icon: <Building2 size={20} />, t: "Penguatan Kelembagaan", d: "Organisasi yang mandiri dan akuntabel." }
                ].map((item, i) => (
                  <div key={i} className="About_misiItem">
                    <div className="About_misiIconWrapper">{item.icon}</div>
                    <div>
                      <h4>{item.t}</h4>
                      <p>{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. VALUES SECTION */}
      <section className="About_valuesSection">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="About_sectionLabel">Nilai & Asas Organisasi</span>
            <h2 className="About_sectionTitle text-slate-900">Fondasi Pergerakan</h2>
            <p className="text-slate-500 italic mt-2 max-w-2xl mx-auto">"Dalam pengelolaan lembaga, program, dan kegiatan, LAKPESDAM secara konsisten menerapkan asas-asas sebagai berikut:"</p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {valuesList.map((value, idx) => (
              <div key={idx} className="About_valueCard_premium">
                <div className="About_valueCard_num">{idx + 1}</div>
                <p className="About_valueCard_text">{value}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
      {/* 5. LEADERSHIP SECTION */}
      <section className="About_leadership_premium" id="structure">
        <div className="container">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="About_sectionLabel">Kepemimpinan</span>
            <h2 className="About_sectionTitle text-slate-900">Susunan Pengurus</h2>
          </motion.div>

          <motion.div
            className="About_leadership_grid_top"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {boardMembers.map((member) => (
              <div key={member.id} className="About_leaderCard_premium">
                <div className="About_leaderImg_wrapper">
                  <img
                    src={member.imageUrl}
                    alt={member.title}
                    className="About_leaderImg"
                    loading="lazy"
                    width="400"
                    height="400"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22400%22%20height%3D%22400%22%20viewBox%3D%220%200%20400%20400%22%3E%3Crect%20fill%3D%22%23064e3b%22%20width%3D%22400%22%20height%3D%22400%22%2F%3E%3Ctext%20fill%3D%22%23ffffff%22%20font-family%3D%22sans-serif%22%20font-size%3D%2260%22%20dy%3D%2220%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3EL%3C%2Ftext%3E%3C%2Fsvg%3E';
                    }}
                  />
                  <div className="About_leaderCard_glow"></div>
                </div>
                <div className="About_leaderInfo_premium">
                  <h3 className="About_leaderName">{member.title}</h3>
                  <div className="About_leaderRole_badge">{member.role || "Pengurus"}</div>
                </div>
              </div>
            ))}
          </motion.div>
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <button onClick={() => navigate('/pengurus')} className="About_btn_outline mx-auto">
              Lihat Seluruh Pengurus <ChevronRight size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* 6. STAFF SECTION - MODERN SLIDER */}
      <section className="About_staff_modern" id="staff">
        <div className="container">
          <div className="About_staff_header">
            <div className="About_staff_header_left">
              <span className="About_staff_badge">Tim Operasional</span>
              <h2 className="About_staff_title">Tim Operasional LAKPESDAM</h2>
            </div>
            <div className="About_staff_header_right">
              <p className="About_staff_description">
                Berfokus pada kebutuhan umat, tim kami menghadirkan solusi yang memadukan pengetahuan mendalam dan strategi mutakhir untuk memastikan pertumbuhan yang berkelanjutan bagi organisasi dan masyarakat.
              </p>
              <div className="About_staff_controls">
                <button
                  onClick={() => setCurrentStaffIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentStaffIndex === 0}
                  className="About_staff_control_btn"
                  aria-label="Geser ke anggota tim sebelumnya"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => setCurrentStaffIndex(prev => Math.min(Math.max(0, staffMembers.length - itemsPerView), prev + 1))}
                  disabled={currentStaffIndex >= staffMembers.length - itemsPerView}
                  className="About_staff_control_btn"
                  aria-label="Geser ke anggota tim selanjutnya"
                >
                  <ChevronRight size={20} />
                </button>
                <button
                  onClick={() => navigate('/staf')}
                  className="About_staff_see_all"
                >
                  Lihat Semua <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>

          <div className="About_staff_slider_container">
            <motion.div
              className="About_staff_grid_modern"
              animate={{ x: `calc(${currentStaffIndex} * (-100% - 1.5rem) / ${itemsPerView})` }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              style={{
                display: 'flex',
                gap: '1.5rem',
                width: '100%'
              }}
            >
              {staffMembers.map(staff => (
                <div key={staff.id} className="About_staff_card_modern" style={{ flex: `0 0 calc((100% - ${(itemsPerView - 1) * 1.5}rem) / ${itemsPerView})` }}>
                  <div className="About_staff_image_container">
                    <img
                      src={staff.imageUrl}
                      alt={staff.title}
                      className="About_staff_image_full"
                      loading="lazy"
                      width="600"
                      height="800"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22800%22%20viewBox%3D%220%200%20600%20800%22%3E%3Crect%20fill%3D%22%23064e3b%22%20width%3D%22600%22%20height%3D%22800%22%2F%3E%3Ctext%20fill%3D%22%23ffffff%22%20font-family%3D%22sans-serif%22%20font-size%3D%2280%22%20dy%3D%2230%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3EL%3C%2Ftext%3E%3C%2Fsvg%3E';
                      }}
                    />
                    <div className="About_staff_overlay">
                      <h3 className="About_staff_name">{staff.title}</h3>
                      <p className="About_staff_role">{staff.role || 'Program Officer'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. NETWORK SECTION */}
      <section className="About_networkSection" id="network">
        <div className="container">
          <div className="About_networkBox">
            <motion.div
              className="About_networkHeader"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="About_networkTitle">Jangkauan Nasional</h2>
              <p>Lakpesdam PBNU memiliki struktur organisasi yang solid dari tingkat pusat hingga pelosok desa.</p>

              <div className="About_networkRegions_tags mt-8">
                {['Jawa Timur', 'Jawa Tengah', 'Jawa Barat', 'Lampung', 'NTB', 'Kalimantan Barat', 'Sulawesi Selatan', 'Papua'].map(region => (
                  <span key={region} className="About_regionTag_premium">
                    <Map size={14} /> {region}
                  </span>
                ))}
              </div>
            </motion.div>
            <motion.div
              className="About_networkGrid"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {[
                { n: "34", l: "Wilayah (PW)", d: "Level Provinsi" },
                { n: "320+", l: "Cabang (PC)", d: "Level Kabupaten/Kota" },
                { n: "1250+", l: "Desa Dampingan", d: "Program Inklusi & Ekonomi" }
              ].map((stat, i) => (
                <div key={i} className="About_networkItem">
                  <span className="About_networkNum" translate="no">{stat.n}</span>
                  <span className="About_networkLabel">{stat.l}</span>
                  <p className="text-xs text-emerald-300 opacity-75 mt-1">{stat.d}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
